import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import React, { useCallback, useEffect, useState } from "react";
import FareInformation from "./fare-details";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import Image from "next/image";

import { SegmentInfo, TotalPriceInfo } from "@/types/search/flight";
import { ChevronDown, ChevronUp, Info, Loader2, Plane } from "lucide-react";
import { set } from "date-fns";
import FlightLine from "./flight-line";
import { useFlightSearch } from "@/hooks/flight/useFlightSearch";
import { useRouter, useSearchParams } from "next/navigation";
import { format, parseISO } from "date-fns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import FareComparison from "../fare-comparison";
import { Card } from "@/components/ui/card";
import { FareDialog } from "./fareDialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { FlightDetailsDialog } from "./flightDetailsDialog";
import FareDetails from "./fareDetails";

interface FlightDetailsCardProps {
  data: any;
  defaultFare: any;
  isMultiCity?: boolean;
  multiCityKey?: string;
  isCombo?: boolean;
  routeDataInfo?: any;
  manualBlockId?: string;
  manualBlockData?: any;
  matchedFlights?: any[];
  isMatched?: boolean;
  matchScore?: number;
  readOnly?: boolean;
  shareId?: string;
}

interface Fare {
  fare: any;
  id: string;
  fd: {
    ADULT: {
      fC: {
        TF: number;
      };
      cc: string;
      mi: boolean;
      rT: number;
    };
  };
}

const FlightDetailsCard: React.FC<FlightDetailsCardProps> = ({
  data,
  isMultiCity,
  multiCityKey,
  defaultFare,
  isCombo,
  routeDataInfo,
  manualBlockId,
  manualBlockData,
  matchedFlights = [],
  isMatched = false,
  matchScore = 0,
  readOnly = false,
  shareId,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [show, setShow] = useState(false);
  const { selectedMulticityFlights, setSelectedFlights }: any =
    useFlightBookingStore();
  const { push } = useRouter();
  const searchParams = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { sI, totalPriceList } = data;
  const flights = sI[0];
  // const { fd } = totalPriceList[0];

  const { getFareRules } = useFlightSearch();

  const formatDateAndTime = (dateTimeString: string) => {
    const dateObj = new Date(dateTimeString);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString("default", { month: "short" });
    const formattedDate = `${day} ${month}`;
    const hours = dateObj.getHours().toString().padStart(2, "0");
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    const formattedTime = `${hours}:${minutes}`;
    return { formattedDate, formattedTime };
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const displayedFares = showAll ? totalPriceList : totalPriceList.slice(0, 1);
  const [selectedFare, setSelectedFare] = useState<TotalPriceInfo>(
    defaultFare || totalPriceList[0],
  );
  const headerFares = readOnly
    ? selectedFare
      ? [selectedFare]
      : totalPriceList.slice(0, 1)
    : displayedFares;

  useEffect(() => {
    setSelectedFare(totalPriceList[0]);
  }, [totalPriceList]);

  const handleFareChange = useCallback(
    async (fare: TotalPriceInfo) => {
      if (readOnly) {
        setSelectedFare(fare);
        return;
      }
      // setSelectedFare(fare);
      if (isMultiCity) {
        await setSelectedFlights("multicity", {
          fare,
          multiCityKey,
          sI: flights,
        });
      }
    },
    [
      setSelectedFlights,
      isMultiCity,
      multiCityKey,
      selectedMulticityFlights,
      flights,
      readOnly,
    ],
  );

  const handleOneWaybookFlight = () => {
    const isReschedule = searchParams.get("reschedule") === "true";
    const oldBookingId = searchParams.get("oldBookingId");

    let url = `/flight-review/${selectedFare?.id}`;
    const params = new URLSearchParams();

    if (isReschedule) {
      params.set("reschedule", "true");
      if (oldBookingId) params.set("oldBookingId", oldBookingId);
      const paxIds = searchParams.get("paxIds");
      if (paxIds) params.set("paxIds", paxIds);
    }

    if (manualBlockId) {
      url += `/manual-block/${manualBlockId}`;
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    push(url);
  };

  const handlerFaresRulesBooks = async (fare: any, totalPrice?: any) => {
    if (isMultiCity) {
      await setSelectedFlights("multicity", {
        fare,
        multiCityKey,
        sI: flights,
      });
    } else {
      const isReschedule = searchParams.get("reschedule") === "true";
      const oldBookingId = searchParams.get("oldBookingId");

      let url = `/flight-review/${fare?.id}`;
      const params = new URLSearchParams();

      if (isReschedule) {
        params.set("reschedule", "true");
        if (oldBookingId) params.set("oldBookingId", oldBookingId);
        const paxIds = searchParams.get("paxIds");
        if (paxIds) params.set("paxIds", paxIds);
      }

      if (manualBlockId) {
        url += `/manual-block/${manualBlockId}`;
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      push(url);
    }
  };

  const handleBookFlight = async () => {
    try {
      await handleOneWaybookFlight();
    } catch (error) {
      console.error("Error booking flight:", error);
    }
  };

  const firstDeparture = sI[0];
  const lastArrival = sI[sI.length - 1];

  const flightDetail = firstDeparture.fD;
  const { da, dt, stops } = firstDeparture;
  const { formattedTime: deparTime, formattedDate: deparDate } =
    formatDateAndTime(dt);
  const { at, aa } = lastArrival;
  const { formattedTime: arriveTime, formattedDate: arriveDate } =
    formatDateAndTime(at);
  const flightImg = `/AirlinesLogo/${flightDetail.aI.code}.png`;

  const totalTravelTimeMinutes =
    firstDeparture.duration +
    (lastArrival.duration ?? 0) +
    (firstDeparture.cT ?? 0);
  // Helper function to get flight identifier
  const getFlightId = (flight: any) => {
    // Try to get ID from totalPriceList first (fare ID)
    if (flight.totalPriceList?.[0]?.id) {
      return flight.totalPriceList[0].id;
    }
    // Fallback to creating ID from segment IDs
    if (flight.sI?.length > 0) {
      return flight.sI.map((segment: any) => segment.id).join("-");
    }
    // Last fallback - use flight number and route
    if (flight.sI?.[0]) {
      const firstSegment = flight.sI[0];
      return `${firstSegment.fD?.fN || "unknown"}-${
        firstSegment.da?.code || "unknown"
      }-${firstSegment.aa?.code || "unknown"}`;
    }
    return "unknown";
  };

  // Check if this flight is a matched flight from manual block
  const isMatchedFlight = isMatched;
  const matchInfo = matchedFlights.find(
    (match) => getFlightId(match.flight) === getFlightId(data),
  );

  // Debug logging for manual block mode
  React.useEffect(() => {
    if (manualBlockId) {
      console.log("Flight details card data:", {
        flightId: getFlightId(data),
        isMatchedFlight,
        matchInfo,
        matchScore,
        matchedFlightsCount: matchedFlights.length,
      });
    }
  }, [
    manualBlockId,
    getFlightId(data),
    isMatchedFlight,
    matchInfo,
    matchScore,
    matchedFlights.length,
  ]);

  // Get match data for this flight
  const getMatchData = () => {
    if (!manualBlockId || !matchedFlights.length) return null;
    return matchedFlights.find(
      (match: any) => getFlightId(match.flight) === getFlightId(data),
    );
  };

  const matchData = getMatchData();

  return (
    <div
      className={`rounded-lg ${!isCombo && "p-4 shadow"} relative ${
        matchData?.isPerfectMatch
          ? "bg-gradient-to-r from-emerald-50 to-green-50 ring-2 ring-emerald-500"
          : matchData?.isExactMatch || matchData?.matchScore === 100
            ? "bg-green-50 ring-2 ring-green-500"
            : matchData
              ? "bg-blue-50 ring-1 ring-blue-300"
              : "bg-white"
      }`}
      data-flight-id={data.id}
    >
      {/* Match Badge */}
      {matchData && (
        <div className="flex float-end">
          {matchData.isPerfectMatch ? (
            <div className="absolute right-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white text-[8px] px-3 py-1 rounded-full font-bold shadow-lg">
              ⭐ PERFECT MATCH
            </div>
          ) : matchData.isExactMatch ? (
            <div className="absolute right-3 bg-green-500 text-white text-[8px] px-2 py-1 rounded-full font-semibold shadow-md">
              EXACT MATCH
            </div>
          ) : ""}
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between">
        <div className="basis-[65%] lg:basis-[75%] mb-2.5 lg:mb-0">
          <div className="flex flex-col justify-between h-[100%] relative">
            <div>
              <div className="py-3 lg:px-3 lg:py-4">
                {sI.length > 1 ? (
                  <div className="flex flex-col md:flex-row md:justify-between items-start gap-2 xl:gap-4">
                    {/* Airline Info */}
                    <div className="flex items-center gap-3 basis-[22%] lg:basis-[18%]">
                      <div className="relative">
                        <Image
                          src={flightImg}
                          alt={flightDetail.aI.name}
                          width={35}
                          height={35}
                          className="rounded-full"
                        />
                        <span className="absolute -bottom-1 -right-1 bg-primary text-white text-[10px] rounded-full px-1.5 py-0.5">
                          {flightDetail.aI.code}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                          {flightDetail.aI.name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {flightDetail.fN}
                        </p>
                      </div>
                    </div>

                    {/* Flight Timeline */}
                    <div className="flex items-start justify-around w-[100%] basis-[100%] md:basis-[70%] lg:basis-[76%]">
                      {/* Departure */}
                      <div className="flex-shrink-0 w-auto xl:w-[100px]">
                        <div className="text-sm lg:text-[18px] font-bold text-gray-900">
                          {deparTime}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                          {deparDate}
                        </div>
                        <div className="text-[11px] sm:text-xs text-gray-600 mt-1">
                          {/* <div>{da.city}</div> */}
                          <div className="flex items-center gap-1 flex-wrap">
                            <span>({da.code})</span>
                          </div>
                        </div>
                      </div>

                      {/* Flight Path */}
                      <div className="flex flex-col items-center px-2 sm:px-4 mx-2 sm:mx-4 flex-shrink-0 mt-1.5 w-[160px] xl:w-[200px]">
                        <span className="text-[10px] sm:text-xs text-primary mb-1 lg:mb-2 font-semibold">
                          {totalTravelTimeMinutes > 0
                            ? formatDuration(totalTravelTimeMinutes)
                            : "0m"}
                        </span>
                        <FlightLine />
                        <span className="text-[10px] sm:text-xs text-gray-700 mt-2 lg:mt-3 font-bold">
                          {sI[0]?.cT ? `${sI.length - 1} ` : "Non"} - Stop
                        </span>
                      </div>

                      <div className="text-right sm:text-center xl:w-[150px] sm:w-auto">
                        <div className="text-sm lg:text-[18px] font-bold text-gray-900">
                          {arriveTime}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                          {arriveDate}
                        </div>
                        <div className="text-[11px] sm:text-xs text-gray-600 mt-1">
                          {/* <div>{aa.city}</div> */}
                          <div className="flex items-center gap-1 justify-end sm:justify-center flex-wrap">
                            <span>({aa.code})</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  sI.map((flight: any, index: number) => {
                    const flightDetails = flight.fD;
                    const { da, dt, at, aa } = flight;
                    const {
                      formattedTime: departureTime,
                      formattedDate: departureDate,
                    } = formatDateAndTime(dt);
                    const {
                      formattedTime: arrivalTime,
                      formattedDate: arrivalDate,
                    } = formatDateAndTime(at);
                    const flightImgs = `/AirlinesLogo/${flightDetails.aI.code}.png`;
                    return (
                      <div
                        key={index}
                        className="flex flex-col md:flex-row md:justify-between items-start gap-2 xl:gap-4"
                      >
                        {/* Airline Info */}
                        <div className="flex items-center gap-3 basis-[22%] lg:basis-[18%]">
                          <div className="relative">
                            <Image
                              src={flightImgs}
                              alt={flightDetails.aI.name}
                              width={40}
                              height={40}
                              className="rounded-full"
                            />
                            <span className="absolute -bottom-1 -right-1 bg-primary text-white text-[10px] rounded-full px-1.5 py-0.5">
                              {flightDetails.aI.code}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                              {flightDetails.aI.name}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {flightDetails.fN}
                            </p>
                          </div>
                        </div>

                        {/* Flight Timeline */}
                        <div className="flex items-start justify-around w-[100%] basis-[100%] md:basis-[70%] lg:basis-[76%]">
                          {/* Departure */}
                          <div className="flex-shrink-0 w-auto xl:w-[100px]">
                            <div className="text-sm lg:text-[18px] font-bold text-gray-900">
                              {departureTime}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                              {departureDate}
                            </div>
                            <div className="text-[11px] sm:text-xs text-gray-600 mt-1">
                              {/* <div>{da.city}</div> */}
                              <div className="flex items-center gap-1 flex-wrap">
                                <span>({da.code})</span>
                              </div>
                            </div>
                          </div>

                          {/* Flight Path */}
                          <div className="flex flex-col items-center px-2 sm:px-4 mx-2 sm:mx-4 flex-shrink-0 mt-1.5 w-[160px] xl:w-[200px]">
                            <span className="text-[10px] sm:text-xs text-primary mb-1 lg:mb-2 font-semibold">
                              {formatDuration(flight.duration)}
                            </span>
                            <FlightLine />
                            <span className="text-[10px] sm:text-xs text-gray-700 mt-2 lg:mt-3 font-bold">
                              {flight?.cT ? `${sI.length - 1} ` : "Non"} - Stop
                            </span>
                          </div>

                          {/* Arrival */}
                          {/* flex-shrink-0 */}
                          <div className="text-right sm:text-center xl:w-[150px] sm:w-auto">
                            <div className="text-sm lg:text-[18px] font-bold text-gray-900">
                              {arrivalTime}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                              {arrivalDate}
                            </div>
                            <div className="text-[11px] sm:text-xs text-gray-600 mt-1">
                              {/* <div>{aa.city}</div> */}
                              <div className="flex items-center gap-1 justify-end sm:justify-center flex-wrap">
                                <span>({aa.code})</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="text-right flex flex-col justify-between items-start gap-1 basis-[25%] pt-3 relative">
          {headerFares.map((fare: any, index: number) => {
            let totalPrice = fare.fd.ADULT.fC.TF;

            return (
              <>
                <div
                  className="flex justify-between items-center w-full"
                  key={index}
                >
                  <div className="flex flex-col mb-1 w-[100%]">
                    <div className="flex space-x-2">
                      {!readOnly && (
                      <input
                        type="radio"
                        id={`fare-${
                          isMultiCity ? "multicity" : "onward"
                        }-${index}`}
                        className="accent-primary appearance-auto"
                        value={fare.fd.ADULT.fC.TF}
                        checked={
                          isMultiCity
                            ? selectedMulticityFlights?.some(
                                (data: Fare) => data?.fare?.id === fare.id,
                              )
                            : selectedFare?.id === fare.id
                        }
                        onChange={() => {
                          handleFareChange(fare);
                          if (show) {
                            getFareRules({ id: fare.id, flowType: "SEARCH" });
                          }
                        }}
                        style={{
                          WebkitAppearance: "radio",
                          appearance: "radio",
                        }}
                      />
                      )}
                      <label
                        htmlFor={`fare-${
                          isMultiCity ? "multicity" : "onward"
                        }-${index}`}
                      >
                        <p className="font-bold text-[#0e1e2b] text-sm lg:text-[20px] mb-1">
                          {new Intl.NumberFormat("en-IN", {
                            style: "currency",
                            currency: "INR",
                          }).format(totalPrice)}
                        </p>
                      </label>
                    </div>
                    <p className="text-[11px] text-gray_lightgray text-start flex flex-col gap-1 "></p>
                    <Separator className="mt-2" />
                  </div>
                </div>
              </>
            );
          })}

          <div className="flex justify-start">
            <Button
              className="text-sm font-bold text-primary bg-white shadow-md border-2 border-primary"
              onClick={() => setShow(!show)}
            >
              {show ? "Hide Details" : "Show Details"}
            </Button>
          </div>
          <p>
            <FlightDetailsDialog sI={sI} />
          </p>
        </div>
      </div>
      <div>
        {selectedFare && (
          <div className="">
            {show && (
              <div className="p-4">
                <Carousel
                  opts={{
                    align: "start",
                    loop: false,
                  }}
                  className="w-full"
                >
                  <CarouselContent className="-ml-2 md:-ml-4">
                    {totalPriceList.map((fares: any) => (
                      <FareDetails
                        fares={fares}
                        key={fares.id}
                        totalPriceList={totalPriceList}
                        isMultiCity={isMultiCity}
                        sI={sI}
                        selectedFare={
                          isMultiCity
                            ? selectedMulticityFlights[Number(multiCityKey)]?.fare
                            : selectedFare
                        }
                        totalTravelTimeMinutes={totalTravelTimeMinutes}
                        handlerFaresRulesBooks={handlerFaresRulesBooks}
                        readOnly={readOnly}
                        shareId={shareId}
                        onSelectFare={readOnly ? setSelectedFare : undefined}
                      />
                    ))}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightDetailsCard;
